import { useState, useEffect, useRef } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { ChatHeader } from "../../../components/chat/chat-header";
import { MessageFooter } from "../../../components/chat/message-footer/message-footer";
import { mockMessage, mockMessages } from "../moked-messages/mock-messages";
import MessageBubble from "../../../components/chat/chat-bubble/message-bubble";
import images from "../../../assets/images";
import {
  ChevronDownIcon,
  MenuEmojiIcon,
  OnboardingArrowTop,
  OnboardingArrowTop2,
  OnboardingArrowTop3,
  OnboardingArrowTop4,
  SpeakerIcon,
} from "../../../assets/icon";
import StaticMessageMenuDropdown from "./static-message-menu-dropdown";
import { mockMenuItems } from "./mockMenuItems";
import { AnimatePresence, motion } from "framer-motion";
import { ProgressBarsBlocks } from "../../../components/progress-bars-blocks/progress-bars-blocks";
import { LeaderBoard } from "../../../components/leader-board/leader-board";

interface ContextMenuOnboardingProps {
  handleNextPage: () => void;
}

export const ContextMenuOnboarding = ({
  handleNextPage,
}: ContextMenuOnboardingProps) => {
  const isTablet = useMediaQuery("(max-width:1368px)");
  const isTabletLandscape = useMediaQuery(
    "(min-width: 768px) and (max-width: 1368px) and (orientation: landscape)",
  );
  const sceneScale = isTablet ? 1 : 1;

  const [showMenu, setShowMenu] = useState(false);
  const [iconChanged, setIconChanged] = useState(false);
  const [thirdVariant, setThirdVariant] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const messageBubbleRef = useRef<HTMLDivElement>(null);
  const handleNextPageRef = useRef(handleNextPage);
  const [isSecondSlide, setIsSecondSlide] = useState(false);
  const [isHideLeaderBoard, setIsHideLeaderBoard] = useState(false);
  const currentUserAvatarRef = useRef<HTMLDivElement | null>(null);
  const [arrowX, setArrowX] = useState(50);
  const [arrowY, setArrowY] = useState(50);

  useEffect(() => {
    handleNextPageRef.current = handleNextPage;
  }, [handleNextPage]);

  useEffect(() => {
    const showTimer = setTimeout(() => {
      setShowMenu(true);
      if (messageBubbleRef.current) {
        const rect = messageBubbleRef.current.getBoundingClientRect();
        const menuWidth = isTabletLandscape ? 220 : 280;
        const menuHeight = isTabletLandscape ? 220 : 300;
        const margin = 8;
        const desiredTop =
          rect.bottom + window.scrollY - (isTabletLandscape ? 260 : 370);
        const horizontalOffset = isTabletLandscape ? 80 : 40;
        const desiredLeft =
          rect.left + window.scrollX + rect.width + horizontalOffset;
        const minTop = window.scrollY + margin;
        const maxTop =
          window.scrollY + window.innerHeight - menuHeight - margin;
        const minLeft = window.scrollX + margin;
        const maxLeft = window.scrollX + window.innerWidth - menuWidth - margin;

        setMenuPosition({
          top: Math.max(minTop, Math.min(desiredTop, maxTop)),
          left: Math.max(minLeft, Math.min(desiredLeft, maxLeft)),
        });
      }
    }, 2000);
    const hideTimer = setTimeout(() => {
      setShowMenu(false);
      setIconChanged(true);
    }, 6000);

    const iconTimer = setTimeout(() => {}, 7000);

    const thirdTimer = setTimeout(() => {
      setThirdVariant(true);
    }, 11000);

    const setIsSecondSlideTimer = setTimeout(() => {
      setIsSecondSlide(true);
    }, 16000);

    const setIsHideLeaderBoardTimer = setTimeout(() => {
      setIsHideLeaderBoard(true);
    }, 21000);

    const setIsNextSlideTimer = setTimeout(() => {
      handleNextPageRef.current();
    }, 25000);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
      clearTimeout(iconTimer);
      clearTimeout(thirdTimer);
      clearTimeout(setIsSecondSlideTimer);
      clearTimeout(setIsHideLeaderBoardTimer);
      clearTimeout(setIsNextSlideTimer);
    };
  }, [isTabletLandscape]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentUserAvatarRef.current) {
        const rect = currentUserAvatarRef.current.getBoundingClientRect();
        setArrowX(rect.left - 73);
        setArrowY(rect.top + 70);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [isSecondSlide, isHideLeaderBoard]);

  const robotVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0 },
  };

  const messageVariants = {
    hidden: { opacity: 0, y: 0 },
    visible: { opacity: 1, y: 0 },
  };

  const menuVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  return (
    <Box
      sx={{
        position: "relative",
        height: "100%",
        minHeight: "100dvh",
        overflowY: "auto",
        overflowX: "hidden",
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(0deg, #50557033 0%, #505570 100%)",
        width: "100%",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          position: "relative",
          minHeight: "900px",
          flex: 1,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
      <Box
        sx={{
          position: "absolute",
          zIndex: -2,
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          filter: "blur(5px)",
          overflow: "hidden",
          "&::-webkit-scrollbar": { display: "none" },
          "-ms-overflow-style": "none",
          "scrollbar-width": "none",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box sx={{ flexShrink: 0 }}>
            <ChatHeader onLogout={() => {}} onMessageSelect={() => {}} />
          </Box>
          <Box
            sx={{
              flex: 1,
              overflowY: "scroll",
              maxWidth: "100%",
              width: "100%",
              margin: "0 auto",
              px: "10px",
              "&::-webkit-scrollbar": { display: "none" },
              "-ms-overflow-style": "none",
              "scrollbar-width": "none",
            }}
          >
            {mockMessages.map((msg, index) => (
              <MessageBubble
                key={msg.message_id}
                message={msg}
                isBot={msg.is_bot}
                bubbleIndex={index}
                headerVisible={false}
                prevMessageHeight={0}
                scrollToPreviousUserMessage={() => {}}
                scrollToNextUserMessage={() => {}}
                scrollToPreviousLessonMessage={() => {}}
                scrollToNextLessonMessage={() => {}}
                isStatic={true}
              />
            ))}
          </Box>
          <Box sx={{ flexShrink: 0 }}>
            <MessageFooter
              onSendMessage={() => {}}
              onRecordAudio={() => {}}
              onFileChange={() => {}}
              isUploading={false}
              isUploadingFile={false}
            />
          </Box>
        </Box>
      </Box>
      <AnimatePresence mode="wait">
        {!isSecondSlide ? (
          <motion.div
            key="firstSlide"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7 }}
            style={{ width: "100%" }}
          >
            <Box
              sx={{
                width: "100%",
                margin: "0 auto",
                position: "relative",
                transform: `scale(${sceneScale})`,
                transformOrigin: "top center",
              }}
            >
              <Box
                component={motion.div}
                initial="hidden"
                animate="visible"
                variants={robotVariants}
                transition={{ duration: 0.5 }}
                sx={{
                  position: "absolute",
                  width: isTabletLandscape
                    ? { xs: "120px", sm: "130px", md: "140px" }
                    : { xs: "170px", sm: "200px", md: "220px" },
                  top: isTabletLandscape
                    ? { xs: 380, sm: 400, md: 420 }
                    : { xs: 520, sm: 580, md: 610 },
                  left: isTabletLandscape
                    ? { xs: -8, sm: -16, md: -24 }
                    : { xs: -24, sm: -50, md: -80 },
                }}
              >
                <img
                  src={images.OnboardingBotSmall}
                  alt="Onboarding Bot Small"
                  style={{ width: "100%" }}
                />
              </Box>

              <AnimatePresence mode="wait">
                {thirdVariant ? (
                  <Box
                    key="sms3"
                    component={motion.div}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    sx={{
                      position: "absolute",
                      width: "220px",
                      top: 544,
                      left: 100,
                      zIndex: 5,
                    }}
                  >
                    <img
                      src={images.OnboardingBotSms3}
                      alt="Onboarding Bot SMS 3"
                      style={{ width: "100%" }}
                    />
                  </Box>
                ) : iconChanged ? (
                  <Box
                    key="sms2"
                    component={motion.div}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    sx={{
                      position: "absolute",
                      width: "220px",
                      top: 544,
                      left: 100,
                      zIndex: 5,
                    }}
                  >
                    <img
                      src={images.OnboardingBotSms2}
                      width={380}
                      height={160}
                      alt="Onboarding Bot SMS 2"
                      style={{ width: "100%" }}
                    />
                  </Box>
                ) : (
                  <Box
                    key="sms"
                    component={motion.div}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                    sx={{
                      position: "absolute",
                      width: "220px",
                      top: 540,
                      left: 98,
                      zIndex: 5,
                    }}
                  >
                    <img
                      src={images.OnboardingBotSms}
                      alt="Onboarding Bot SMS"
                      style={{ width: "100%" }}
                    />
                  </Box>
                )}
              </AnimatePresence>

              <Box
                component={motion.div}
                initial="hidden"
                animate="visible"
                variants={messageVariants}
                transition={{ delay: 0.5, duration: 0.5 }}
                sx={{
                  position: "absolute",
                  top: 217,
                  left: 8,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Box ref={messageBubbleRef}>
                  <MessageBubble
                    key={mockMessage.message_id}
                    message={mockMessage}
                    isBot={mockMessage.is_bot}
                    bubbleIndex={1}
                    headerVisible={false}
                    prevMessageHeight={0}
                    scrollToPreviousUserMessage={() => {}}
                    scrollToNextUserMessage={() => {}}
                    scrollToPreviousLessonMessage={() => {}}
                    scrollToNextLessonMessage={() => {}}
                    isStatic
                    isOneMessage
                  />
                </Box>
                <AnimatePresence mode="wait">
                  {iconChanged ? (
                    <motion.div
                      key="speaker"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <SpeakerIcon size={25} color="#A0A3B1" />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="menu"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <MenuEmojiIcon width={25} height={25} fill="#A0A3B1" />
                    </motion.div>
                  )}
                </AnimatePresence>

                <AnimatePresence>
                  {showMenu && (
                    <Box
                      component={motion.div}
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                      variants={menuVariants}
                      transition={{ duration: 0.5 }}
                      sx={{
                        position: "absolute",
                        top: menuPosition.top,
                        left: menuPosition.left,
                      }}
                    >
                      <StaticMessageMenuDropdown
                        menuItems={mockMenuItems}
                        text="This is a mock text"
                        anchorPosition={menuPosition}
                      />
                    </Box>
                  )}
                </AnimatePresence>
              </Box>
              <AnimatePresence mode="wait">
                {!thirdVariant ? (
                  <Box
                    key="arrow1"
                    component={motion.div}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                    sx={{
                      position: "absolute",
                      top: 300,
                      left: 188,
                    }}
                  >
                    <OnboardingArrowTop width="131px" />
                  </Box>
                ) : (
                  <Box
                    key="arrow2"
                    component={motion.div}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    sx={{
                      position: "absolute",
                      top: 330,
                      left: 15,
                    }}
                  >
                    <OnboardingArrowTop2 width="220px" />
                  </Box>
                )}
              </AnimatePresence>
            </Box>
          </motion.div>
        ) : (
          <motion.div
            key="secondSlide"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7 }}
            style={{ width: "100%" }}
          >
            <Box
              sx={{
                width: "100%",
                position: "relative",
                transform: `scale(${sceneScale})`,
                transformOrigin: "top center",
              }}
            >
              <Box
                style={{
                  position: "absolute",
                  top: 0,
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 100,
                  border: "none",
                  cursor: "pointer",
                  background: "#FFFFFF",
                  borderBottomLeftRadius: "5px",
                  borderBottomRightRadius: "5px",
                }}
              >
                <ChevronDownIcon
                  size={24}
                  style={{
                    color: "#092347",
                    transform: isHideLeaderBoard
                      ? "rotate(0deg)"
                      : "rotate(180deg)",
                    transition: "transform 0.3s",
                  }}
                />
              </Box>
              <AnimatePresence mode="wait">
                {!isHideLeaderBoard && (
                  <motion.div
                    key="leaderboard-block"
                    initial={{ y: 0, opacity: 1 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{
                      y: -150,
                      opacity: 0,
                      transition: { duration: 0.8, ease: "easeInOut" },
                    }}
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                    style={{
                      position: "relative",
                      background: "#FFFFFF",
                      paddingBottom: "10px",
                    }}
                  >
                    <ChatHeader
                      onLogout={() => {}}
                      onMessageSelect={() => {}}
                    />
                    <ProgressBarsBlocks isStatic />
                    <LeaderBoard
                      isStatic
                      currentUserAvatarRef={currentUserAvatarRef}
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence mode="wait">
                {!isHideLeaderBoard && (
                  <motion.div
                    key="second-part"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{
                      opacity: 0,
                      transition: { duration: 0.8, ease: "easeInOut" },
                    }}
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                  >
                    <Box
                      sx={{
                        maxWidth: "100%",
                        width: "100%",
                        margin: "0 auto",
                        position: "relative",
                      }}
                    >
                      <Box
                        key="arrow1"
                        component={motion.div}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{
                          opacity: 0,
                          transition: { duration: 0.8, ease: "easeInOut" },
                        }}
                        transition={{
                          duration: 0.5,
                          delay: 0.5,
                          ease: "easeInOut",
                        }}
                        sx={{
                          position: "fixed",
                          top: arrowY,
                          left: arrowX,
                        }}
                      >
                        <Box
                          key="sms"
                          component={motion.div}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{
                            opacity: 0,
                            transition: { duration: 0.8, ease: "easeInOut" },
                          }}
                          transition={{
                            delay: 0.5,
                            duration: 0.5,
                            ease: "easeInOut",
                          }}
                          sx={{
                            position: "absolute",
                            width: "220px",
                            top: 240,
                            left: 0,
                            zIndex: 5,
                          }}
                        >
                          <img
                            src={images.OnboardingBotSms4}
                            alt="Onboarding Bot SMS"
                            style={{ width: "100%" }}
                          />
                        </Box>
                        <OnboardingArrowTop3 width="171px" />
                        <Box
                          component={motion.div}
                          initial="hidden"
                          animate="visible"
                          variants={robotVariants}
                          transition={{ duration: 0.5 }}
                          sx={{
                            position: "absolute",
                            width: isTabletLandscape
                              ? { xs: "120px", md: "140px" }
                              : { xs: "170px", md: "220px" },
                            top: isTabletLandscape
                              ? { xs: 120, md: 100 }
                              : { xs: 250, md: 300 },
                            left: isTabletLandscape
                              ? { xs: -60, md: -90 }
                              : { xs: -95, md: -150 },
                          }}
                        >
                          <img
                            src={images.OnboardingBotSmall}
                            alt="Onboarding Bot Small"
                            style={{ width: "100%" }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>
          </motion.div>
        )}
        {isHideLeaderBoard && (
          <AnimatePresence mode="wait">
            <motion.div
              key="hide-leaderboard-fragment"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{
                opacity: 0,
                transition: { duration: 0.8, ease: "easeInOut" },
              }}
              transition={{ duration: 0.5, ease: "easeInOut", delay: 1 }}
            >
              <Box
                sx={{
                  maxWidth: "100%",
                  width: "100%",
                  margin: "0 auto",
                  position: "relative",
                  transform: `scale(${sceneScale})`,
                  transformOrigin: "top center",
                }}
              >
                <Box
                  component={motion.div}
                  initial="hidden"
                  animate="visible"
                  variants={robotVariants}
                  transition={{ duration: 0.5 }}
                  sx={{
                    position: "absolute",
                    width: isTabletLandscape
                      ? { xs: "120px", md: "140px" }
                      : { xs: "170px", md: "220px" },
                    top: isTabletLandscape
                      ? { xs: 150, md: 100 }
                      : { xs: 320, md: 290 },
                    left: isTabletLandscape
                      ? { xs: -64, md: -220 }
                      : { xs: -110, md: -320 },
                  }}
                >
                  <img
                    src={images.OnboardingBotSmall}
                    alt="Onboarding Bot Small"
                    style={{ width: "100%" }}
                  />
                </Box>

                <Box
                  key="arrow1"
                  component={motion.div}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{
                    opacity: 0,
                    transition: { duration: 0.8, ease: "easeInOut" },
                  }}
                  transition={{ duration: 0.5, delay: 0.5, ease: "easeInOut" }}
                  sx={{
                    position: "absolute",
                    top: 40,
                    left: -78,
                  }}
                >
                  <Box
                    key="sms"
                    component={motion.div}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{
                      opacity: 0,
                      transition: { duration: 0.8, ease: "easeInOut" },
                    }}
                    transition={{
                      delay: 0.5,
                      duration: 0.5,
                      ease: "easeInOut",
                    }}
                    sx={{
                      position: "absolute",
                      width: "220px",
                      top: 175,
                      left: -35,
                      zIndex: 5,
                    }}
                  >
                    <img
                      src={images.OnboardingBotSms5}
                      alt="Onboarding Bot SMS"
                      style={{ width: "100%" }}
                    />
                  </Box>
                  <OnboardingArrowTop4 width="150px" />
                </Box>
              </Box>
            </motion.div>
          </AnimatePresence>
        )}
      </AnimatePresence>
      </Box>
    </Box>
  );
};

export default ContextMenuOnboarding;
